import { useCallback } from "react";

/**
 * Scroll callback used by landing-style pages that don't necessarily contain
 * every section the AppHeader / footer / in-page CTAs may reference.
 *
 * - If the section exists on the current page, scroll to it.
 * - Otherwise navigate to the standalone page for that section, falling back
 *   to the homepage anchor when no dedicated page has been wired up yet.
 *
 * The dedicated-page map mirrors the routes registered in `config/routes.rb`
 * for the marketing landing-section pages. Keep them in sync when adding
 * a new top-level marketing route.
 */
const SECTION_TO_PATH: Record<string, string> = {
  features: "/features",
  pricing: "/pricing",
  "why-toolkit": "/why-toolkit",
  trades: "/trades",
  testimonials: "/testimonials",
  "website-vs-field-service": "/different",
};

/** When the section is not on the page, where the header/footer should send users. */
export function landingSectionNavigationHref(
  id: string,
  hasElement: boolean,
): string | null {
  if (hasElement) return null;
  if (id === "home") return "/";
  return SECTION_TO_PATH[id] ?? `/#${id}`;
}

/** Scroll in-page when the section exists; otherwise go to the marketing route. */
export function landingScrollToSection(id: string): void {
  if (typeof document === "undefined") return;

  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
    return;
  }

  const href = landingSectionNavigationHref(id, false);
  if (href) window.location.href = href;
}

export const useLandingScrollToSection = (): ((id: string) => void) =>
  useCallback((id: string) => {
    landingScrollToSection(id);
  }, []);
